const NON_NUMERIC_KEYS = [
  '频段', '频率', 'MHz', '频点', '范围', '方向', '备注', '通道', '开始[MHz]', '终止[MHz]', '序号', '原始公式', '差值'
];

const deepCloneTables = (tables = []) => tables.map((table) => JSON.parse(JSON.stringify(table)));

const shouldSkipField = (key = '') => NON_NUMERIC_KEYS.some((token) => key.includes(token));

const isValidNumericValue = (value) => {
  const numericValue = parseFloat(value);
  return !isNaN(numericValue) && isFinite(numericValue) && typeof value !== 'boolean' && value !== null && value !== '';
};

const roundToTwoDecimals = (num) => Number(num.toFixed(2));

const transformRowValues = (row = {}, transformFn) => {
  const nextRow = { ...row };

  Object.keys(nextRow).forEach((key) => {
    if (shouldSkipField(key)) {
      return;
    }

    const value = nextRow[key];
    const numericValue = parseFloat(value);

    if (isValidNumericValue(value)) {
      nextRow[key] = roundToTwoDecimals(transformFn(numericValue));
    }
  });

  return nextRow;
};

export const cloneOriginalTables = (tables = []) => deepCloneTables(tables);

export const isEditableNumericCell = (columnKey = '', value = null) => !shouldSkipField(columnKey) && isValidNumericValue(value);

export const processTablesByType = (tables = [], transformationType = 'original', options = {}) => {
  const normalizedTables = deepCloneTables(tables);

  if (transformationType === 'original') {
    return normalizedTables;
  }

  const offset = Number(options.offset ?? 0);
  const transformerMap = {
    opposite: (num) => -num,
    absolute: (num) => Math.abs(num),
    negative: (num) => -Math.abs(num),
    increase: (num) => num + offset,
    decrease: (num) => num - offset
  };

  const transformFn = transformerMap[transformationType];
  if (!transformFn) {
    return normalizedTables;
  }

  return normalizedTables.map((table) => ({
    ...table,
    rows: Array.isArray(table.rows) ? table.rows.map((row) => transformRowValues(row, transformFn)) : []
  }));
};

export const applyCellEditToTables = (tables = [], cell = {}, value) => tables.map((table, tableIndex) => {
  if (tableIndex !== cell.tableIndex) {
    return table;
  }
  const recalculateDiffColumns = (row = {}) => {
    const nextRow = { ...row };
    const diffColumns = Object.keys(nextRow).filter((key) => key.includes('差值('));
    if (!diffColumns.length) {
      return nextRow;
    }

    const valueColumns = Object.keys(nextRow).filter(
      (key) => !shouldSkipField(key) && !key.includes('差值(') && isValidNumericValue(nextRow[key])
    );
    if (valueColumns.length < 2) {
      return nextRow;
    }

    const baseA = parseFloat(nextRow[valueColumns[0]]);
    const baseB = parseFloat(nextRow[valueColumns[1]]);
    if (!Number.isFinite(baseA) || !Number.isFinite(baseB)) {
      diffColumns.forEach((diffKey) => {
        nextRow[diffKey] = null;
      });
      return nextRow;
    }

    const diffValue = Number((baseA - baseB).toFixed(4));
    diffColumns.forEach((diffKey) => {
      nextRow[diffKey] = diffValue;
    });
    return nextRow;
  };

  const nextRows = (table.rows || []).map((row, rowIndex) => {
    if (rowIndex !== cell.rowIndex) {
      return row;
    }

    if (!isEditableNumericCell(cell.columnKey, row[cell.columnKey])) {
      return row;
    }

    const editedRow = {
      ...row,
      [cell.columnKey]: value
    };
    return recalculateDiffColumns(editedRow);
  });

  return {
    ...table,
    rows: nextRows
  };
});

export const canUseOneClickProcessing = (documentType) => documentType === 'device-calibration';